import React, { useState, useRef, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogIn } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState(null);
  const recaptchaRef = useRef(null);
  const { user, login } = useAuth();

  // Buscar configurações do backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get('/api/config');
        setRecaptchaSiteKey(response.data.recaptcha_site_key);
      } catch (error) {
        console.error('Erro ao buscar configurações:', error);
        // Fallback para chave de teste
        setRecaptchaSiteKey("6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI");
      }
    };

    fetchConfig();
  }, []);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Verificar se o reCAPTCHA foi resolvido
    if (!recaptchaToken) {
      setError('Por favor, complete a verificação reCAPTCHA');
      setLoading(false);
      return;
    }

    const result = await login(email, password, recaptchaToken);

    if (!result.success) {
      setError(result.error);
      // Reset reCAPTCHA em caso de erro
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white sm:bg-gradient-to-br sm:from-blue-900 sm:to-indigo-900">
      <div className="min-h-screen flex items-center justify-center p-0 sm:p-8">
        <div className="w-full max-w-md">
          <div className="bg-white min-h-screen sm:min-h-0 sm:rounded-lg sm:shadow-xl p-6 sm:p-8 flex flex-col justify-center">
            <div className="text-center">
              <div className="mx-auto h-10 mt-4 rounded-full flex items-center justify-center">
                <img src="https://repo.telic.net.br/imgdir/Tglobal_360x100px_2.png" />
              </div>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="mt-1 appearance-none relative block w-full px-3 py-3 sm:py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 text-base sm:text-sm"
                    placeholder="Digite seu email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="mt-1 appearance-none relative block w-full px-3 py-3 sm:py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 text-base sm:text-sm"
                    placeholder="Digite sua senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              {/* reCAPTCHA */}
              {recaptchaSiteKey && (
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSiteKey}
                    onChange={handleRecaptchaChange}
                    onExpired={() => setRecaptchaToken(null)}
                    onError={() => setRecaptchaToken(null)}
                  />
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading || !recaptchaToken}
                  className="group relative w-full flex justify-center py-3 sm:py-2 px-4 border border-transparent text-base sm:text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <LogIn className="h-5 w-5 sm:h-4 sm:w-4 mr-2" />
                      Entrar
                    </>
                  )}
                </button>
              </div>

              <div className="text-center">
                <a
                  href="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Esqueceu sua senha?
                </a>
              </div>
            </form>

            <div className="mt-6 text-center text-xs text-gray-500">
              <p>Em caso de problema, entre em contato com suporte@tglobal.cloud</p>
            </div>

            {/* Footer Mobile */}
            <div className="text-center mt-6 sm:mt-0">
              <p className="text-xs inline sm:hidden text-gray-500 opacity-75">
                © 2025 CloudControl - Powered by TGlobal
              </p>
            </div>
          </div>
          {/* Footer Desktop */}
          <div className="text-center sm:mt-4">
            <p className="text-xs hidden sm:inline text-blue-100 opacity-75">
              © 2025 CloudControl - Powered by TGlobal
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;